import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getTimesheetSessions } from '../../../services/timesheetService';
import formatMinutes from '../../../utils/formatMinutes';

const Wrap = styled.div`
  padding: 8px 4px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
  th, td {
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid #eef2f6;
  }
  th { color: #64748b; font-weight: 600; }
`;

const Muted = styled.div`
  color: #64748b;
  font-size: 13px;
`;

const Failed = styled.div`
  color: #b91c1c;
  font-size: 13px;
`;

// TR duvar saati "SS:dd" (oturum anları UTC); başka güne düşerse "gg.AA SS:dd".
const wallClock = (iso, rowDate, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat(lang || 'tr', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const time = `${parts.hour}:${parts.minute}`;
  const day = `${parts.year}-${parts.month}-${parts.day}`;
  return rowDate && day !== String(rowDate).slice(0, 10) ? `${parts.day}.${parts.month} ${time}` : time;
};

const place = (gate, reader) => [gate, reader].filter(Boolean).join(' · ') || '-';

/**
 * AKM POC §7.6 — puantaj listesinde satır açılınca o günün BÜTÜN giriş-çıkış oturumları (kapı, okuyucu, süre). Gece
 * vardiyasının oturumu başladığı güne atanır. api.js GET'te reddetmez: res.data.status === false → "yüklenemedi".
 */
export default function DaySessionsRow({ userId, date }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, failed: false, rows: [] });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, failed: false, rows: [] });
    Promise.resolve(getTimesheetSessions(userId, String(date).slice(0, 10)))
      .then((res) => {
        if (cancelled) return;
        if (!res?.data || res.data.status === false) setState({ loading: false, failed: true, rows: [] });
        else setState({ loading: false, failed: false, rows: Array.isArray(res.data.data) ? res.data.data : [] });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, failed: true, rows: [] }); });
    return () => { cancelled = true; };
  }, [userId, date]);

  if (state.loading) return <Wrap><Muted>{t('common.loading')}</Muted></Wrap>;
  if (state.failed) return <Wrap><Failed role="alert">{t('taReports.sessions.loadFailed')}</Failed></Wrap>;
  if (state.rows.length === 0) return <Wrap><Muted>{t('taReports.sessions.empty')}</Muted></Wrap>;

  return (
    <Wrap data-testid="day-sessions">
      <Table>
        <thead>
          <tr>
            <th>{t('taReports.sessions.entry')}</th>
            <th>{`${t('taReports.sessions.gate')} / ${t('taReports.sessions.reader')}`}</th>
            <th>{t('taReports.sessions.exit')}</th>
            <th>{`${t('taReports.sessions.gate')} / ${t('taReports.sessions.reader')}`}</th>
            <th>{t('taReports.sessions.duration')}</th>
          </tr>
        </thead>
        <tbody>
          {state.rows.map((s) => (
            <tr key={`${s.entryTime}-${s.exitTime || 'open'}`}>
              <td>{wallClock(s.entryTime, date, i18n.language)}</td>
              <td>{place(s.entryGateName, s.entryAccessPointName)}</td>
              <td>{s.exitTime ? wallClock(s.exitTime, date, i18n.language) : t('taReports.sessions.open')}</td>
              <td>{s.exitTime ? place(s.exitGateName, s.exitAccessPointName) : '-'}</td>
              <td>{s.durationMinutes != null ? formatMinutes(s.durationMinutes) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Wrap>
  );
}
